import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck,
  ActivitySquare,
  Target,
  AlertTriangle,
  PlayCircle,
  Timer,
  CircleDot,
  Stethoscope,
  Briefcase,
  ListChecks,
  FileText,
  Network,
  Layers,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  ALIGN_COLOR,
  RISK_TONE,
  SummaryCell,
  deriveBusinessImpact,
  deriveRootCause,
} from "@/components/cre/execSummary";

export default function ExecutiveSummary({ incident, mttrOverride, readOnly }) {
  const [mttr, setMttr] = useState(mttrOverride || null);
  const hasRunbook = Boolean(incident?.runbook);
  const approvalKey = incident?.approval?.decision || "none";

  useEffect(() => {
    let cancelled = false;
    if (mttrOverride) {
      setMttr(mttrOverride);
      return;
    }
    if (!incident?.id || !hasRunbook || readOnly) {
      setMttr(null);
      return;
    }
    api
      .mttrReport(incident.id)
      .then((r) => !cancelled && setMttr(r))
      .catch(() => !cancelled && setMttr(null));
    return () => {
      cancelled = true;
    };
  }, [incident?.id, hasRunbook, approvalKey, mttrOverride, readOnly]);

  if (!incident) return null;

  const consensus = incident.consensus;
  const guardrail = incident.guardrail;
  const top = consensus?.top_action;
  const root = deriveRootCause(incident);
  const biz = deriveBusinessImpact(incident);
  const hasAnalysis = Boolean(consensus && incident.runbook);

  const align = consensus ? Number(consensus.alignment_score || 0) : null;
  const topRisk = top?.risk || "—";
  const overallRisk = guardrail?.overall_risk || topRisk;
  const flagsCount = guardrail?.flags?.length || 0;
  const riskMismatch =
    guardrail && top && guardrail.overall_risk !== top.risk;

  return (
    <section
      data-testid="executive-summary"
      className="cre-surface rounded-sm p-5 md:p-6"
    >
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="cre-overline text-blue-300">CRE assessment</span>
          <Badge
            variant="outline"
            className="rounded-sm font-mono text-[10px] border-blue-500/30 text-blue-300 bg-blue-500/5"
          >
            Advisory — human validation required
          </Badge>
          {incident.domain_classification ? (
            <Badge
              variant="outline"
              data-testid="exec-domain-chip"
              className={`rounded-sm font-mono text-[10px] ${
                incident.domain_classification.confidence >= 0.55
                  ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
                  : incident.domain_classification.confidence >= 0.4
                  ? "border-amber-500/40 text-amber-300 bg-amber-500/5"
                  : "border-rose-500/40 text-rose-300 bg-rose-500/5"
              }`}
              title={incident.domain_classification.rationale}
            >
              <Layers className="h-3 w-3 mr-1 -mt-0.5 inline" strokeWidth={1.75} />
              {incident.domain_classification.label} ·{" "}
              {Math.round(incident.domain_classification.confidence * 100)}%
            </Badge>
          ) : null}
        </div>
        {!hasAnalysis ? (
          <span className="text-[11px] font-mono text-zinc-500">
            Run agent dispatch to populate this assessment.
          </span>
        ) : null}
      </div>

      {/* Low domain confidence banner */}
      {incident.domain_classification &&
      incident.domain_classification.confidence < 0.4 ? (
        <div
          data-testid="exec-domain-fallback-banner"
          className="mb-4 border border-rose-500/30 bg-rose-500/[0.06] rounded-sm p-3 text-[11px] font-mono text-rose-200 leading-relaxed"
        >
          Domain-specific confidence is limited. CRE is using generic incident
          reasoning. Recommendations should be validated by the relevant
          service owner before use.
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* 1 — Incident summary */}
        <SummaryCell
          icon={FileText}
          label="Incident summary"
          tone="text-zinc-300"
          tid="exec-incident-summary"
          value={
            <span className="text-zinc-200 text-xs leading-snug block">
              {incident.description?.trim() || "No description provided."}
            </span>
          }
        />

        {/* 2 — Affected systems and symptoms */}
        <SummaryCell
          icon={Network}
          label="Affected systems & symptoms"
          tone="text-sky-300"
          tid="exec-affected-systems"
          value={
            <div className="space-y-1.5 text-xs leading-snug">
              {incident.affected_system ? (
                <div>
                  <span className="cre-overline text-zinc-500">Systems</span>
                  <div className="text-zinc-200 font-mono break-words">
                    {incident.affected_system}
                  </div>
                </div>
              ) : null}
              {incident.symptoms?.length ? (
                <div>
                  <span className="cre-overline text-zinc-500">Symptoms</span>
                  <div className="text-zinc-200">
                    {incident.symptoms.join(" · ")}
                  </div>
                </div>
              ) : null}
              {incident.telemetry_hints ? (
                <div>
                  <span className="cre-overline text-zinc-500">Telemetry</span>
                  <div className="text-zinc-300 font-mono text-[11px] break-words line-clamp-3">
                    {incident.telemetry_hints}
                  </div>
                </div>
              ) : null}
              {!incident.affected_system &&
              !incident.symptoms?.length &&
              !incident.telemetry_hints ? (
                <span className="text-zinc-500">—</span>
              ) : null}
            </div>
          }
        />

        {/* 3 — Business impact */}
        <SummaryCell
          icon={Briefcase}
          label="Business impact"
          tone="text-amber-300"
          tid="exec-business-impact"
          value={
            <>
              <span className="text-zinc-100 text-sm leading-snug block">
                {biz.text}
              </span>
              {biz.agent ? (
                <span className="block mt-1 text-[11px] font-mono text-zinc-500">
                  {biz.agent}
                </span>
              ) : null}
            </>
          }
        />

        {/* 4 — Likely root cause */}
        <SummaryCell
          icon={Stethoscope}
          label="Likely root cause"
          tone="text-blue-300"
          tid="exec-root-cause"
          highlight={root?.lowConfidence}
          value={
            root ? (
              <>
                <span className="text-zinc-100 leading-snug block">
                  {root.label}
                </span>
                <span className="block mt-1 text-[11px] font-mono text-zinc-500">
                  {root.detail}
                </span>
              </>
            ) : (
              <span className="text-zinc-500">Pending analysis</span>
            )
          }
        />

        {/* 5 — Recommended action */}
        <SummaryCell
          icon={CircleDot}
          label="Recommended action"
          tone="text-violet-300"
          tid="exec-recommended"
          value={
            top ? (
              <>
                <span className="text-zinc-100 leading-snug block">
                  {top.action}
                </span>
                <span className="block mt-1 text-[11px] font-mono text-zinc-500">
                  by {top.proposer_agent} · {top.category}
                </span>
              </>
            ) : (
              <span className="text-zinc-500">Pending</span>
            )
          }
        />

        {/* 6 — Consensus alignment */}
        <SummaryCell
          icon={ActivitySquare}
          label="Consensus alignment"
          tone="text-emerald-300"
          tid="exec-alignment"
          value={
            align !== null ? (
              <>
                <span
                  className={`font-heading text-3xl font-black tracking-tighter ${ALIGN_COLOR(align)}`}
                >
                  {(align * 100).toFixed(0)}%
                </span>
                <span className="block mt-0.5 text-[11px] font-mono text-zinc-500">
                  across {incident.hypotheses?.length || 0} agents
                </span>
              </>
            ) : (
              <span className="text-zinc-500">—</span>
            )
          }
        />

        {/* 7 — Risk level */}
        <SummaryCell
          icon={AlertTriangle}
          label="Risk level"
          tone="text-rose-300"
          tid="exec-risk"
          value={
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono text-zinc-500 uppercase">
                  Guardrail
                </span>
                <Badge
                  variant="outline"
                  className={`rounded-sm font-mono uppercase ${RISK_TONE[overallRisk] || "border-white/10 text-zinc-400"}`}
                  data-testid="exec-risk-guardrail"
                >
                  {overallRisk}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono text-zinc-500 uppercase">
                  Top action
                </span>
                <Badge
                  variant="outline"
                  className={`rounded-sm font-mono uppercase ${RISK_TONE[topRisk] || "border-white/10 text-zinc-400"}`}
                  data-testid="exec-risk-top"
                >
                  {topRisk}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-[11px] font-mono text-zinc-500">
                <span>Flags</span>
                <span data-testid="exec-risk-flags" className="text-zinc-300">
                  {flagsCount}
                </span>
              </div>
              {riskMismatch ? (
                <div
                  data-testid="exec-risk-mismatch"
                  className="text-[10px] font-mono text-amber-400 mt-1"
                >
                  · Guardrail risk differs from top action risk
                </div>
              ) : null}
            </div>
          }
        />

        {/* 8 — Execution mode */}
        <SummaryCell
          icon={PlayCircle}
          label="Execution mode"
          tone="text-rose-300"
          tid="exec-mode"
          value={
            <>
              <span className="text-zinc-100">Advisory only</span>
              <span className="block mt-0.5 text-[11px] font-mono text-zinc-500">
                Human validation required
              </span>
            </>
          }
        />

        {/* 9 — Human approval */}
        <SummaryCell
          icon={ShieldCheck}
          label="Human approval"
          tone="text-blue-300"
          tid="exec-approval"
          value={
            <>
              <span className="text-zinc-100">Required</span>
              <span className="block mt-0.5 text-[11px] font-mono text-zinc-500">
                {incident.approval
                  ? `Decided: ${incident.approval.decision.toUpperCase()}`
                  : "Pending reviewer"}
              </span>
            </>
          }
        />

        {/* 10 — Est. MTTR saved */}
        <SummaryCell
          icon={Timer}
          label="Est. MTTR saved"
          tone={mttr?.is_illustrative ? "text-amber-300" : "text-emerald-300"}
          tid="exec-mttr"
          value={
            mttr ? (
              <>
                <span
                  className={`font-heading text-3xl font-black tracking-tighter ${
                    mttr.is_illustrative
                      ? "text-amber-300"
                      : "text-emerald-300"
                  }`}
                >
                  {Math.round(mttr.minutes_saved)}
                </span>
                <span
                  className={`ml-1 font-mono text-xs ${
                    mttr.is_illustrative
                      ? "text-amber-300/70"
                      : "text-emerald-300/70"
                  }`}
                >
                  min
                </span>
                <span className="block mt-0.5 text-[11px] font-mono text-zinc-500">
                  {mttr.percent_saved}% vs manual baseline ·{" "}
                  <span
                    data-testid="exec-mttr-tier"
                    className={
                      mttr.is_illustrative
                        ? "text-amber-400"
                        : mttr.confidence_tier === "indicative"
                        ? "text-sky-400"
                        : "text-emerald-400"
                    }
                  >
                    {mttr.confidence_tier || "credible"}
                  </span>
                </span>
              </>
            ) : (
              <span className="text-zinc-500">After analysis</span>
            )
          }
        />

        {/* 11 — Status */}
        <SummaryCell
          icon={Target}
          label="Status"
          tone="text-zinc-300"
          tid="exec-status"
          value={
            <>
              <span className="text-zinc-100 capitalize">
                {incident.status.replace(/_/g, " ")}
              </span>
              {incident.archived ? (
                <span className="block mt-0.5 text-[11px] font-mono text-zinc-500">
                  · archived
                </span>
              ) : null}
            </>
          }
        />
      </div>
    </section>
  );
}

